import {
  AvatarNext as Avatar,
  Flex,
  PresenceType,
  SelectNext as Select,
  Text,
} from '@momentum-ui/react-collaboration';
import { Item } from '@react-stately/collections';
import { IWebexIntContact } from '@webex/component-adapter-interfaces/dist/esm/src';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logger } from '../../../../logger/src/Logger';
import { usePresenceContext } from '../../contexts/PresenceContext';
import { useLazyLoadAvatarUrl } from '../../hooks/useLazyLoadAvatarUrl';
import useWebexClasses from '../../hooks/useWebexClasses';
import { removeBracketsAndContent } from '../../utils/avatarInitials';
import { resolveWebexUserId } from '../../utils/webexUserIdMapper'; // ✅ added
import { CallButtons } from '../CallButtons/CallButtons';
import './ContactCard.styles.scss';

type Props = {
  style?: React.CSSProperties;
  user: IWebexIntContact;
  noCallableAddressMessage?: string;
  makeCall?: (address: string, isVideo?: boolean, label?: string) => void;
};

/**
 * Description for this component.
 *
 * @param {Props} props Props for this component
 * @param props.style optional styles to add
 * @param props.user user to display call options for
 * @param props.onSelect callback function to call when the selection is updated.  It is called once at component mount with the default value
 * @param props.noCallableAddressMessage message to display when there is no callable entry for a user
 * @returns Contact card react component
 */
export const ContactCard = ({
  user,
  style = undefined,
  noCallableAddressMessage = 'No addresses found',
  makeCall
}: Props) => {
  const [cssClasses, sc] = useWebexClasses('contact-card');
  const [imageUrl] = useLazyLoadAvatarUrl(user.id, user.fetchAvatarUrl);
  const { t } = useTranslation();
  const { manager, getPresence, version: presenceVersion } = usePresenceContext();

  /* ------------------------------------------------------------------ */
  /*                 RESOLVE WEBEX USER ID FOR PRESENCE                 */
  /* ------------------------------------------------------------------ */

  const [webexUserId, setWebexUserId] = useState<string | undefined>();

  useEffect(() => {
    let mounted = true;

    const resolveIdentity = async () => {
      const resolved = await resolveWebexUserId({
        contactId: user.id,
        displayName: user.name,
        email: user.emailAddresses?.[0]?.address,
        phone: user.phoneNumbers?.[0]?.address,
      });

      if (mounted) {
        setWebexUserId(resolved);
      }
    };

    resolveIdentity();

    return () => {
      mounted = false;
    };
  }, [user.id, user.name, user.emailAddresses, user.phoneNumbers]);

  /* ------------------------------------------------------------------ */
  /*                 SUBSCRIBE TO PRESENCE MANAGER                      */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!webexUserId || !manager) return;

    const featureKey = `contactCardModal-${webexUserId}`;

    // Register feature and immediately fetch presence if not already available
    const registerAndFetch = async () => {
      // First, check if presence already exists (from other features like Speed Dial or Call History)
      // This provides instant presence if the user was already subscribed
      const existingPresence = manager.getPresence(webexUserId);
      
      // Register the feature (this will subscribe for real-time updates)
      await manager.registerFeature(featureKey, [webexUserId]);
      
      // If presence wasn't available, force immediate fetch
      // This ensures Contact Card gets presence data instantly instead of waiting
      if (!existingPresence) {
        await manager.fetchPresenceImmediately(webexUserId);
      }
    };

    registerAndFetch();

    return () => {
      manager.unregisterFeature(featureKey).catch((error) => {
        Logger.error(`[ContactCard] Failed to unregister: ${error instanceof Error ? error.message : error}`);
      });
    };
  }, [webexUserId, manager]);

  /* ------------------------------------------------------------------ */
  /*                      GET PRESENCE STATE                             */
  /* ------------------------------------------------------------------ */

  const presence = useMemo(() => {
    if (!webexUserId || !manager) return undefined;
    
    // Check presence immediately (may be cached from other features)
    const presenceData = manager.getPresence(webexUserId);
    
    return presenceData;
  }, [webexUserId, manager, getPresence, presenceVersion]);

  // Call options: phone/extension/SIP only (no email), to match Webex calling UI
  const callables = useMemo(
    () => [...user.phoneNumbers ?? [], ...user.emailAddresses],
    [user]
  );
  const chatLabel = '1:1 chat';
  const [selectedAddress, setSelectedAddress] = useState(callables[0]?.address);
  const callSelectElement =
    callables.length > 1 ? (
      <Select
        className={sc('select')}
        onSelectionChange={(address) => setSelectedAddress(address.toString())}
        defaultSelectedKey={callables[0]?.address}
        aria-label="select address to call"
      >
        {callables.map((entity) => (
          <Item key={entity.address}>
            {`${t('label.address.' + entity.type)}: ${entity.address}`}
          </Item>
        ))}
      </Select>
    ) : (
      callables[0]?.address ? (
        <Text type="body-secondary" className={sc('text-address')}>
        {t('label.address.' + callables[0]?.type)}: {callables[0]?.address}
        </Text>
      ) : (
        <Text type="body-secondary" className={sc('text-address')}>
          {noCallableAddressMessage}
        </Text>
      )
    );

  return (
    <Flex
      className={cssClasses}
      style={style}
      direction="column"
      alignItems="center"
      ygap="15px"
    >
      <Avatar
        initials={removeBracketsAndContent(user.name)}
        title={user.name}
        size={88}
        src={imageUrl}
        className={sc('avatar')}
        presence={presence?.presenceType ?? PresenceType.Default}
        presenceLabel={presence?.presenceLabel}
      />
      <Text type="banner-secondary" className={sc('name')}>
        {user.name}
      </Text>
      {callSelectElement}
      <CallButtons 
       address={selectedAddress} 
       className={sc('call-buttons')}
       useMakeAudioCall={() => makeCall && makeCall(selectedAddress, false, chatLabel)}
       useMakeVideoCall={() => makeCall && makeCall(selectedAddress, true, chatLabel)}
      />
    </Flex>
  );
};
